import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import logger from "../../../art-logger-config";
import { AppThunk } from "../../app/store";
import { searchAggregations } from "../../app/api";
import {
  AggregationFacet,
  AggregationResult,
  SearchAggregationResult,
} from "../../../from-server/asin-central/search";
import { STATUS_ATTRIBUTE_SPECS } from "./chartData";

export type StatusOverviewAggregationStatus =
  | "idle"
  | "loading"
  | "success"
  | "error";

export interface StatusOverviewAggregationState {
  status: StatusOverviewAggregationStatus;
  totalCount: number;
  aggregationResults: AggregationResult;
  // Earliest known audit timestamp per validation service, so the
  // Stale Validation Data alert can call out exactly which service
  // (LVS, Hattrick, or both) is stale instead of a generic message.
  earliestLvsRunAtMs: number | undefined;
  earliestHattrickRunAtMs: number | undefined;
  error: string | null;
  lastFetchedQueryString: string | null;
}

const initialState: StatusOverviewAggregationState = {
  status: "idle",
  totalCount: 0,
  aggregationResults: {},
  earliestLvsRunAtMs: undefined,
  earliestHattrickRunAtMs: undefined,
  error: null,
  lastFetchedQueryString: null,
};

// Each status attribute has ~3–4 distinct values; 10 leaves headroom
// while keeping response payloads small.
const MAX_BUCKETS_PER_ATTRIBUTE = 10;

// Per-service request-audit attributes whose `.lastUpdatedAt`
// timestamps the backend surfaces as scalar MIN aggregations. Every
// other freshness check in the app (validationAttributeModal,
// submitCatalogDefaultsConnector) reads the request audit only, so
// we do the same here. The names MUST match registered DACS
// attributes — DACS resolves `<attributeName>.lastUpdatedAt`
// server-side for MIN facets, so we send the raw attribute
// (e.g. `validationRequestLVS`), not the client-side "Audits"
// wrapper name (`validationRequestLVSAudits`).
const LVS_REQUEST_AUDIT_ATTRIBUTE = "validationRequestLVS";
const HATTRICK_REQUEST_AUDIT_ATTRIBUTE = "validationRequestHattrick";
const VALIDATION_AUDIT_ATTRIBUTES = [
  LVS_REQUEST_AUDIT_ATTRIBUTE,
  HATTRICK_REQUEST_AUDIT_ATTRIBUTE,
] as const;

function coerceScalarToTimestampMs(value: unknown): number | undefined {
  if (typeof value !== "number") return undefined;
  if (!Number.isFinite(value)) return undefined;
  if (value <= 0) return undefined;
  return value;
}

const statusOverviewAggregation = createSlice({
  name: "statusOverviewAggregation",
  initialState,
  reducers: {
    statusOverviewAggregationStart(
      state: StatusOverviewAggregationState,
      { payload }: PayloadAction<{ queryString: string }>
    ) {
      // Fresh filter: wipe the prior bars so the container shows its
      // spinner ("Loading status overview…") instead of leaving stale
      // bars visible while the new aggregation runs.
      if (payload.queryString !== state.lastFetchedQueryString) {
        state.aggregationResults = {};
        state.totalCount = 0;
      }
      state.status = "loading";
      state.error = null;
      state.lastFetchedQueryString = payload.queryString;
      state.earliestLvsRunAtMs = undefined;
      state.earliestHattrickRunAtMs = undefined;
    },
    statusOverviewAggregationSuccess(
      state: StatusOverviewAggregationState,
      { payload }: PayloadAction<SearchAggregationResult>
    ) {
      state.status = "success";
      state.totalCount = payload.totalResultCount ?? 0;
      state.aggregationResults = payload.aggregationResults ?? {};
      state.earliestLvsRunAtMs = coerceScalarToTimestampMs(
        payload.scalarAggregationResults?.[LVS_REQUEST_AUDIT_ATTRIBUTE]
      );
      state.earliestHattrickRunAtMs = coerceScalarToTimestampMs(
        payload.scalarAggregationResults?.[HATTRICK_REQUEST_AUDIT_ATTRIBUTE]
      );
      state.error = null;
    },
    statusOverviewAggregationFailure(
      state: StatusOverviewAggregationState,
      { payload }: PayloadAction<string>
    ) {
      state.status = "error";
      state.error = payload;
      state.earliestLvsRunAtMs = undefined;
      state.earliestHattrickRunAtMs = undefined;
    },
    resetStatusOverviewAggregation() {
      return initialState;
    },
  },
});

export const {
  statusOverviewAggregationStart,
  statusOverviewAggregationSuccess,
  statusOverviewAggregationFailure,
  resetStatusOverviewAggregation,
} = statusOverviewAggregation.actions;

export default statusOverviewAggregation.reducer;

function buildStatusAggregationFacets(): AggregationFacet[] {
  return [
    ...STATUS_ATTRIBUTE_SPECS.map((spec) => ({
      attributeName: spec.attributeKey,
      aggregationType: "terms",
      totalAggregationCount: MAX_BUCKETS_PER_ATTRIBUTE,
    })),
    ...VALIDATION_AUDIT_ATTRIBUTES.map((attr) => ({
      attributeName: attr,
      aggregationType: "min",
      totalAggregationCount: 0,
    })),
  ];
}

// Guards against out-of-order responses: only commit `success` /
// `failure` when this thunk owns the most recent
// `lastFetchedQueryString`. A slower response to an older query is
// dropped so it can't overwrite fresher data.
export const fetchStatusOverviewAggregation =
  (queryString: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(statusOverviewAggregationStart({ queryString }));
    try {
      const result = await searchAggregations(
        queryString,
        buildStatusAggregationFacets()
      );
      if (
        getState().statusOverviewAggregation.lastFetchedQueryString ===
        queryString
      ) {
        dispatch(statusOverviewAggregationSuccess(result));
      }
    } catch (err: unknown) {
      if (
        getState().statusOverviewAggregation.lastFetchedQueryString ===
        queryString
      ) {
        const message = err instanceof Error ? err.message : String(err);
        logger.error(`Status overview aggregation request failed: ${message}`);
        dispatch(statusOverviewAggregationFailure(message));
      }
    }
  };
